import { useState } from "react";
import { List, DataTable, CreateButton } from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Phone,
  Mail,
  Briefcase,
  DollarSign,
  Users,
  Clock,
  Tag,
} from "lucide-react";
import { useListContext } from "ra-core";
import type { Contact } from "../../providers/fakerest/dataGenerator/types";
import {
  CONTACT_SOURCES,
  getContactSourceLabel,
  getContactSourceBadgeClass,
  getContactSourceIcon,
} from "../constants";
import { TopToolbar } from "../../layout/TopToolbar";
import {
  FilterSidebar,
  FilterButton,
  type FilterSection,
  type FilterOption,
} from "../../filters/FilterSidebar";

// Column header with icon helper
const ColHeader = ({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) => (
  <span className="flex items-center gap-1.5">
    <Icon className="h-4 w-4 text-muted-foreground" />
    {label}
  </span>
);

const SourceBadge = (record: Contact) => {
  const Icon = getContactSourceIcon(record.source);
  return (
    <Badge
      className={`${getContactSourceBadgeClass(record.source)} flex items-center gap-1.5`}
    >
      <Icon className="h-3 w-3" />
      {getContactSourceLabel(record.source)}
    </Badge>
  );
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const TotalValueCell = (record: Contact) => (
  <span className="font-medium">{formatCurrency(record.total_value)}</span>
);

// Filter configuration
const sourceSection: FilterSection = {
  id: "source",
  label: "Source",
  icon: <Users className="h-4 w-4" />,
  type: "multi",
  source: "source",
  options: CONTACT_SOURCES.map((source) => ({
    value: source.id,
    label: source.label,
    icon: <source.icon className={`h-4 w-4 ${source.color}`} />,
  })),
};

const filterSections: FilterSection[] = [sourceSection];

const sortOptions: FilterOption[] = [
  { value: "name:ASC", label: "Name A-Z" },
  { value: "name:DESC", label: "Name Z-A" },
  { value: "created_at:DESC", label: "Newest first" },
  { value: "created_at:ASC", label: "Oldest first" },
  { value: "total_value:DESC", label: "Highest value" },
  { value: "total_value:ASC", label: "Lowest value" },
];

const ContactListActions = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const { filterValues } = useListContext();

  const activeFilterCount = Object.entries(filterValues || {}).filter(
    ([key, value]) => key !== "q" && value !== undefined && value !== "",
  ).length;

  return (
    <>
      <TopToolbar>
        <FilterButton
          onClick={() => setFilterOpen(true)}
          activeCount={activeFilterCount}
        />
        <CreateButton />
      </TopToolbar>
      <FilterSidebar
        open={filterOpen}
        onOpenChange={setFilterOpen}
        sections={filterSections}
        sortOptions={sortOptions}
      />
    </>
  );
};

export const ContactList = () => (
  <List actions={<ContactListActions />} perPage={1000} pagination={false}>
    <DataTable<Contact> rowClick="show">
      <DataTable.Col
        source="name"
        label={<ColHeader icon={User} label="Name" />}
        headerClassName="w-[200px]"
        cellClassName="w-[200px]"
      />
      <DataTable.Col
        source="phone"
        label={<ColHeader icon={Phone} label="Phone" />}
        headerClassName="w-[140px]"
        cellClassName="w-[140px]"
      />
      <DataTable.Col
        source="email"
        label={<ColHeader icon={Mail} label="Email" />}
        headerClassName="w-[250px]"
        cellClassName="w-[250px]"
      />
      <DataTable.Col
        source="source"
        label={<ColHeader icon={Users} label="Source" />}
        render={SourceBadge}
        headerClassName="w-[150px]"
        cellClassName="w-[150px]"
      />
      <DataTable.NumberCol
        source="job_count"
        label={<ColHeader icon={Briefcase} label="Jobs" />}
        headerClassName="w-[80px]"
        cellClassName="w-[80px]"
      />
      <DataTable.Col
        source="total_value"
        label={<ColHeader icon={DollarSign} label="Total Value" />}
        render={TotalValueCell}
        headerClassName="text-right w-[120px]"
        cellClassName="text-right w-[120px]"
      />
    </DataTable>
  </List>
);
